import { useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { green, red, amber, grey } from '@mui/material/colors';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPenToSquare } from '@fortawesome/pro-light-svg-icons';

function AccountTasks({ reminders, setReminders, onSave, users, me }) {
    const [showCompleted, setShowCompleted] = useState(false);
    const [editDialog, setEditDialog] = useState(null); // null or reminder object

    const tasks = (reminders || []);
    const openTasks = tasks.filter(r => r.status === 'open');
    const completedTasks = tasks.filter(r => r.status === 'completed' || r.status === 'rejected');
    const displayTasks = showCompleted ? tasks : openTasks;
    const overdueTasks = openTasks.filter(r => r.dueDate && new Date(r.dueDate) < new Date());

    function onToggleStatus(task) {
        const newStatus = task.status === 'open' ? 'completed' : 'open';
        const updated = reminders.map(r => r.id === task.id ? { ...r, status: newStatus } : r);
        setReminders(updated);
        onSave(updated);
    }

    function onDelete(task) {
        const updated = reminders.filter(r => r.id !== task.id);
        setReminders(updated);
        onSave(updated);
    }

    function onAddTask() {
        setEditDialog({
            id: uuidv4(),
            description: '',
            dueDate: dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
            status: 'open',
            assignee: me?.email || '',
            createdBy: me?.email || '',
            isNew: true,
        });
    }

    function onEditTask(task) {
        setEditDialog({
            ...task,
            dueDate: task.dueDate ? dayjs(task.dueDate).format('YYYY-MM-DDTHH:mm') : '',
        });
    }

    function onSaveTask(task) {
        const saved = {
            id: task.id,
            description: task.description,
            dueDate: task.dueDate ? new Date(task.dueDate).toISOString() : undefined,
            status: task.status || 'open',
            assignee: task.assignee || me?.email,
            createdBy: task.createdBy || me?.email,
        };
        let updated;
        if (task.isNew) {
            updated = [...reminders, saved];
        } else {
            updated = reminders.map(r => r.id === task.id ? saved : r);
        }
        setReminders(updated);
        onSave(updated);
        setEditDialog(null);
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Tasks
                    </Typography>
                    {openTasks.length > 0 && (
                        <Chip label={`${openTasks.length} open`} size='small' sx={{ fontSize: '10px', height: '18px', backgroundColor: overdueTasks.length > 0 ? red[50] : grey[100], color: overdueTasks.length > 0 ? red[700] : grey[700] }} />
                    )}
                    {completedTasks.length > 0 && (
                        <Typography
                            sx={{ fontSize: '11px', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                            onClick={() => setShowCompleted(!showCompleted)}
                        >
                            {showCompleted ? 'Hide completed' : `Show ${completedTasks.length} completed`}
                        </Typography>
                    )}
                </Box>
                <Button size='small' onClick={onAddTask} sx={{ textTransform: 'none', fontSize: '11px' }}>
                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '10px' }} />&nbsp;Add Task
                </Button>
            </Box>
            {displayTasks.length === 0 && (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', py: 1 }}>No open tasks</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {_.sortBy(displayTasks, r => r.status === 'open' ? 0 : 1, r => r.dueDate || '9999').map(task => {
                    const isOverdue = task.status === 'open' && task.dueDate && new Date(task.dueDate) < new Date();
                    const isDone = task.status === 'completed' || task.status === 'rejected';
                    return (
                        <Box key={task.id} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            py: 0.5, px: 1,
                            borderRadius: '6px',
                            backgroundColor: isOverdue ? red[50] : isDone ? grey[50] : 'transparent',
                            '&:hover': { backgroundColor: isOverdue ? red[50] : grey[50] },
                            '&:hover .task-actions': { opacity: 1 },
                        }}>
                            <Checkbox
                                size='small'
                                checked={isDone}
                                onChange={() => onToggleStatus(task)}
                                sx={{ p: 0.5, color: isOverdue ? red[400] : grey[400] }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: '12px', fontWeight: 500,
                                    textDecoration: isDone ? 'line-through' : 'none',
                                    color: isDone ? grey[400] : '#334155',
                                }}>
                                    {task.description || 'Untitled task'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {task.dueDate && (
                                        <Typography sx={{ fontSize: '10px', color: isOverdue ? red[600] : '#94a3b8', fontWeight: isOverdue ? 600 : 400 }}>
                                            {isOverdue ? 'Overdue · ' : ''}{dayjs(task.dueDate).format('MMM D, h:mm A')}
                                        </Typography>
                                    )}
                                    {task.assignee && task.assignee !== me?.email && (
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                            → {task.assignee.split('@')[0]}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box className='task-actions' sx={{ opacity: 0, display: 'flex', gap: 0.5, transition: 'opacity 0.15s' }}>
                                <IconButton size='small' onClick={() => onEditTask(task)} sx={{ color: grey[400], '&:hover': { color: grey[700] } }}>
                                    <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '12px' }} />
                                </IconButton>
                                <IconButton size='small' onClick={() => onDelete(task)} sx={{ color: grey[400], '&:hover': { color: red[500] } }}>
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {editDialog && (
                <TaskDialog
                    task={editDialog}
                    onSave={onSaveTask}
                    onClose={() => setEditDialog(null)}
                    users={users}
                />
            )}
        </Box>
    );
}

function TaskDialog({ task, onSave, onClose, users }) {
    const [form, setForm] = useState(task);

    return (
        <Dialog open={true} onClose={onClose} maxWidth='sm' fullWidth sx={{ zIndex: 100000000 }}>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
                {task.isNew ? 'New Task' : 'Edit Task'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                <TextField
                    autoFocus
                    label='Task description'
                    fullWidth
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                />
                <TextField
                    label='Due date & time'
                    type='datetime-local'
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.dueDate}
                    onChange={e => setForm({ ...form, dueDate: e.target.value })}
                />
                <Box>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>Assign to</Typography>
                    <Select
                        fullWidth
                        size='small'
                        value={form.assignee || ''}
                        onChange={e => setForm({ ...form, assignee: e.target.value })}
                        MenuProps={{ style: { zIndex: 100000001 } }}
                    >
                        {(users || []).map(u => (
                            <MenuItem key={u} value={u}>{u.split('@')[0]}</MenuItem>
                        ))}
                    </Select>
                </Box>
                {!task.isNew && (
                    <Box>
                        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>Status</Typography>
                        <Select
                            fullWidth
                            size='small'
                            value={form.status || 'open'}
                            onChange={e => setForm({ ...form, status: e.target.value })}
                            MenuProps={{ style: { zIndex: 100000001 } }}
                        >
                            <MenuItem value='open'>Open</MenuItem>
                            <MenuItem value='completed'>Completed</MenuItem>
                            <MenuItem value='rejected'>Cancelled</MenuItem>
                        </Select>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined'>Cancel</Button>
                <Button onClick={() => onSave(form)} variant='contained' disabled={!form.description?.trim()}>
                    {task.isNew ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AccountTasks;
